/**
 * FN-FE-15 — Dokumentumtár
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { get, patch, del, downloadFile, previewFile } from '../../lib/api';
import { PageHeader, Button, Input, Modal, EmptyState } from '../../components/ui';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import { useToast } from '../../components/ui/Toast';

// ── Types ──

interface Document {
  id: string;
  name: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  uploader_name: string;
  created_at: string;
  kind: string;
  linked_type?: string | null;
  linked_id?: string | null;
  linked_name?: string | null;
}

// ── File type SVG icons ──

// Maps extension → SVG icon filename in /file-icons/
const FILE_ICON_MAP: Record<string, string> = {
  PDF:  'pdf-svgrepo-com.svg',
  DOC:  'doc-svgrepo-com.svg',
  DOCX: 'doc-svgrepo-com.svg',
  ODT:  'doc-svgrepo-com.svg',
  ODF:  'doc-svgrepo-com.svg',
  XLS:  'xls-svgrepo-com.svg',
  XLSX: 'xls-svgrepo-com.svg',
  ODS:  'xls-svgrepo-com.svg',
  CSV:  'csv-svgrepo-com.svg',
  PNG:  'png-svgrepo-com.svg',
  JPG:  'jpg-svgrepo-com.svg',
  JPEG: 'jpg-svgrepo-com.svg',
  GIF:  'png-svgrepo-com.svg',
  WEBP: 'png-svgrepo-com.svg',
  SVG:  'svg-svgrepo-com.svg',
  TXT:  'txt-svgrepo-com.svg',
  ZIP:  'zip-svgrepo-com.svg',
  RAR:  'zip-svgrepo-com.svg',
  MP4:  'mp4-svgrepo-com.svg',
  AVI:  'avi-svgrepo-com.svg',
  MP3:  'mp3-svgrepo-com.svg',
  HTML: 'html-svgrepo-com.svg',
  CSS:  'css-svgrepo-com.svg',
  PPT:  'ppt-svgrepo-com.svg',
  PPTX: 'ppt-svgrepo-com.svg',
  AI:   'ai-svgrepo-com.svg',
  PSD:  'photoshop-svgrepo-com.svg',
  XML:  'xml-svgrepo-com.svg',
};

const FILE_FALLBACK_COLORS: Record<string, string> = {
  DEFAULT: '#6B7280',
};

function FileIcon({ ext, size = 40 }: { ext: string; size?: number }) {
  const e = ext.toUpperCase();
  const iconFile = FILE_ICON_MAP[e];
  const h = Math.round(size * 1.25);

  if (iconFile) {
    return (
      <img
        src={`/file-icons/${iconFile}`}
        alt={e}
        width={size}
        height={h}
        style={{ objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }

  // Fallback: colored label box
  const label = (e.slice(0, 4) || 'FILE');
  const fontSize = Math.round(size * (label.length > 3 ? 0.22 : 0.27));
  return (
    <div style={{
      width: size,
      height: h,
      background: FILE_FALLBACK_COLORS.DEFAULT,
      borderRadius: Math.round(size * 0.1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      userSelect: 'none',
    }}>
      <span style={{ color: '#fff', fontSize, fontWeight: 700, fontFamily: 'system-ui, sans-serif', letterSpacing: '-0.5px', lineHeight: 1 }}>{label}</span>
    </div>
  );
}

// ── Helpers ──

function getExt(name: string): string {
  const parts = (name || '').split('.');
  return parts.length > 1 ? (parts.pop() || '') : '';
}

function formatSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Main Component ──

export default function DocumentsPage() {
  const { toast } = useToast();
  const [docs, setDocs] = useState<Document[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showPreview, setShowPreview] = useState<Document | null>(null);
  const [showRename, setShowRename] = useState<Document | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const q = params.toString() ? `?${params}` : '';
      const data = await get<any>(`/documents${q}`);
      setDocs(data.data || (Array.isArray(data) ? data : []));
      setTotal(data.total || (Array.isArray(data) ? data.length : 0));
    } catch { /* silent */ }
    setLoading(false);
  }, [search]);

  useEffect(() => { const t = setTimeout(loadDocs, 300); return () => clearTimeout(t); }, [loadDocs]);

  useFKeyBar([
    { key: 'F2', label: 'Feltöltés', action: () => setShowUpload(true) },
    { key: 'F5', label: 'Frissít', action: loadDocs },
  ]);

  const uploadFile = async (file: File) => {
    if (file.size > 25 * 1024 * 1024) { toast(`${file.name} túl nagy (max 25 MB).`, 'error'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/documents', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) toast(`${file.name} feltöltve.`, 'success');
      else toast(`${file.name} feltöltése sikertelen.`, 'error');
    } catch { toast('Feltöltés sikertelen.', 'error'); }
  };

  const handlePageDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const files = e.dataTransfer.files;
    for (let i = 0; i < files.length; i++) await uploadFile(files[i]);
    loadDocs();
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`Törlöd: ${doc.name}?`)) return;
    try { await del(`/documents/${doc.id}`); toast('Törölve.', 'success'); loadDocs(); }
    catch { toast('Törlés sikertelen.', 'error'); }
  };

  const handleRename = async () => {
    if (!showRename || !renameValue.trim()) return;
    try {
      await patch(`/documents/${showRename.id}`, { name: renameValue.trim() });
      toast('Átnevezve.', 'success'); setShowRename(null); loadDocs();
    } catch { toast('Átnevezés sikertelen.', 'error'); }
  };

  const filteredDocs = typeFilter ? docs.filter(d => {
    const mime = (d.mime_type || '').toLowerCase();
    const ext = getExt(d.name || d.original_filename).toLowerCase();
    if (typeFilter === 'pdf') return mime.includes('pdf') || ext === 'pdf';
    if (typeFilter === 'image') return mime.startsWith('image') || ['png','jpg','jpeg','gif','webp','svg'].includes(ext);
    if (typeFilter === 'spreadsheet') return mime.includes('spreadsheet') || mime.includes('excel') || ['xls','xlsx','csv','ods'].includes(ext);
    if (typeFilter === 'document') return mime.includes('word') || mime.includes('document') || ['doc','docx','odt','odf','txt'].includes(ext);
    return true;
  }) : docs;

  return (
    <div
      style={{ position: 'relative' }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handlePageDrop}
    >
      {/* Drag overlay */}
      {dragOver && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: 'rgba(244,123,32,0.08)',
          border: '4px dashed var(--orange)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: '#fff', borderRadius: 'var(--r)', padding: '32px 48px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>📄</div>
            <div style={{ fontWeight: 700, fontSize: '18px', color: 'var(--navy)' }}>Húzd ide a fájlt</div>
            <div style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '4px' }}>Maximum 25 MB / fájl</div>
          </div>
        </div>
      )}

      <PageHeader
        title="Dokumentumtár"
        description={`${total} dokumentum`}
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="chips">
              <button onClick={() => setView('grid')} className={`chip${view === 'grid' ? ' active' : ''}`}>Rács</button>
              <button onClick={() => setView('list')} className={`chip${view === 'list' ? ' active' : ''}`}>Lista</button>
            </div>
            <Button onClick={() => setShowUpload(true)}>+ Feltöltés</Button>
          </div>
        }
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Keresés dokumentumok között..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', maxWidth: '360px' }}
        />
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="form-input"
          style={{ width: '160px', fontSize: '13px' }}
        >
          <option value="">Minden típus</option>
          <option value="pdf">PDF</option>
          <option value="image">Kép</option>
          <option value="spreadsheet">Táblázat (XLS, ODS…)</option>
          <option value="document">Dokumentum (DOC, ODT…)</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--slate)' }}>Betöltés...</div>
      ) : filteredDocs.length === 0 ? (
        <EmptyState
          icon="📂"
          title="Nincs dokumentum"
          description="Töltsd fel az első dokumentumod, vagy húzd az oldalra a fájlt."
          action={<Button onClick={() => setShowUpload(true)}>+ Feltöltés</Button>}
        />
      ) : view === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px',
        }}>
          {filteredDocs.map(d => {
            const ext = getExt(d.original_filename || d.name);
            return (
              <div
                key={d.id}
                onClick={() => setShowPreview(d)}
                style={{
                  background: '#fff',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r)',
                  padding: '16px 12px 10px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--orange)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)'; }}
              >
                <FileIcon ext={ext} size={44} />
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--navy)', wordBreak: 'break-word', lineHeight: 1.3, maxWidth: '100%' }}>
                  {d.name || d.original_filename}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{formatSize(d.size_bytes)}</div>
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => downloadFile(`/documents/${d.id}/download`, d.original_filename || d.name).catch(() => toast('Letöltés sikertelen.', 'error'))}
                    style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--line)', borderRadius: '5px', background: 'none', cursor: 'pointer', color: 'var(--slate)' }}
                  >↓</button>
                  <button
                    onClick={() => { setRenameValue(d.name); setShowRename(d); }}
                    style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--line)', borderRadius: '5px', background: 'none', cursor: 'pointer', color: 'var(--slate)' }}
                  >✎</button>
                  <button
                    onClick={() => handleDelete(d)}
                    style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--line)', borderRadius: '5px', background: 'none', cursor: 'pointer', color: '#dc2626' }}
                  >✕</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* LIST VIEW */
        <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--line)', background: 'var(--mist)' }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Név</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Típus</th>
                <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Méret</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Feltöltő</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Dátum</th>
                <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--slate)', fontSize: '12px' }}>Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((d, i) => {
                const ext = getExt(d.original_filename || d.name);
                return (
                  <tr
                    key={d.id}
                    onClick={() => setShowPreview(d)}
                    style={{
                      borderBottom: i < filteredDocs.length - 1 ? '1px solid var(--line)' : undefined,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--mist)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ''}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FileIcon ext={ext} size={24} />
                        <span style={{ fontWeight: 600, color: 'var(--navy)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.name || d.original_filename}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--slate)' }}>{ext.toUpperCase() || '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--slate)', textAlign: 'right' }}>{formatSize(d.size_bytes)}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--slate)' }}>{d.uploader_name || '—'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--slate)' }}>{formatDate(d.created_at)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <Button size="sm" variant="ghost" onClick={() => downloadFile(`/documents/${d.id}/download`, d.original_filename || d.name).catch(() => toast('Letöltés sikertelen.', 'error'))}>Letöltés</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setRenameValue(d.name); setShowRename(d); }}>Átnevez</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(d)} style={{ color: '#dc2626' }}>Törlés</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onUpload={async (file, linkType, linkId) => {
          const formData = new FormData();
          formData.append('file', file);
          if (linkType) formData.append('linkedType', linkType);
          if (linkId) formData.append('linkedId', linkId);
          try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch('/api/documents', {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: formData,
            });
            if (res.ok) { toast(`${file.name} feltöltve.`, 'success'); setShowUpload(false); loadDocs(); }
            else { toast('Feltöltés sikertelen.', 'error'); }
          } catch { toast('Feltöltés sikertelen.', 'error'); }
        }}
      />

      {/* Preview Modal */}
      <PreviewModal doc={showPreview} onClose={() => setShowPreview(null)} toast={toast} />

      {/* Rename Modal */}
      <Modal isOpen={!!showRename} onClose={() => setShowRename(null)} title="Átnevezés">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input label="Új név" value={renameValue} onChange={e => setRenameValue(e.target.value)} autoFocus />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setShowRename(null)}>Mégse</Button>
            <Button onClick={handleRename}>Mentés</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Upload Modal ──

function UploadModal({ isOpen, onClose, onUpload }: {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, linkType?: string, linkId?: string) => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [linkType, setLinkType] = useState('');
  const [linkId, setLinkId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [partners, setPartners] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      setFile(null); setLinkType(''); setLinkId('');
      get<any>('/partners').then(d => setPartners(Array.isArray(d) ? d : d?.data || [])).catch(() => {});
      get<any>('/projects').then(d => setProjects(Array.isArray(d) ? d : d?.data || [])).catch(() => {});
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    await onUpload(file, linkType || undefined, linkId || undefined);
    setUploading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Dokumentum feltöltése">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) setFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? 'var(--orange)' : 'var(--line)'}`,
            borderRadius: 'var(--r)',
            padding: '32px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? 'rgba(244,123,32,0.04)' : 'var(--mist)',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) setFile(e.target.files[0]); }} />
          {file ? (
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '15px' }}>{file.name}</div>
              <div style={{ color: 'var(--slate)', fontSize: '12px', marginTop: '4px' }}>{formatSize(file.size)}</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
              <div style={{ fontSize: '13px', color: 'var(--slate)' }}>Húzd ide a fájlt vagy <strong>tallózz</strong></div>
              <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '4px', opacity: 0.7 }}>Maximum 25 MB / fájl</div>
            </div>
          )}
        </div>

        {/* Link type */}
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--slate)', marginBottom: '8px' }}>Kapcsolódás</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[
              { val: '', label: 'Önálló dokumentum' },
              { val: 'partner', label: 'Partner-hez' },
              { val: 'project', label: 'Projekt-hez' },
            ].map(opt => (
              <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="linkType" checked={linkType === opt.val} onChange={() => { setLinkType(opt.val); setLinkId(''); }} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {linkType === 'partner' && (
          <select value={linkId} onChange={e => setLinkId(e.target.value)} className="form-input">
            <option value="">Válassz partnert...</option>
            {partners.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {linkType === 'project' && (
          <select value={linkId} onChange={e => setLinkId(e.target.value)} className="form-input">
            <option value="">Válassz projektet...</option>
            {projects.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSubmit} loading={uploading} disabled={!file}>Feltöltés</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Preview Modal ──

function PreviewModal({ doc, onClose, toast }: {
  doc: Document | null;
  onClose: () => void;
  toast: (msg: string, variant: string) => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!doc) { setBlobUrl(null); setLoadError(false); return; }
    const ext = getExt(doc.original_filename || doc.name).toLowerCase();
    const isPdf = ext === 'pdf' || (doc.mime_type || '').includes('pdf');
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || (doc.mime_type || '').startsWith('image');
    if (!isPdf && !isImage) { setBlobUrl(null); return; }

    setLoading(true);
    let revoked = false;
    previewFile(`/documents/${doc.id}/preview`)
      .then(url => { if (!revoked) { setBlobUrl(url); setLoading(false); } else URL.revokeObjectURL(url); })
      .catch(() => { if (!revoked) { setLoadError(true); setLoading(false); } });
    return () => { revoked = true; };
  }, [doc]);

  const handleClose = useCallback(() => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null); setLoadError(false);
    onClose();
  }, [blobUrl, onClose]);

  if (!doc) return null;

  const ext = getExt(doc.original_filename || doc.name).toLowerCase();
  const isPdf = ext === 'pdf' || (doc.mime_type || '').includes('pdf');
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || (doc.mime_type || '').startsWith('image');
  const canPreview = isPdf || isImage;

  return (
    <Modal isOpen={!!doc} onClose={handleClose} title={doc.name || doc.original_filename} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Preview area */}
        <div style={{ minHeight: '240px', background: 'var(--mist)', borderRadius: 'var(--r)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {!canPreview ? (
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <FileIcon ext={ext} size={56} />
              <div style={{ marginTop: '12px', color: 'var(--slate)', fontSize: '13px' }}>
                Előnézet nem érhető el ehhez a fájltípushoz.
              </div>
            </div>
          ) : loadError ? (
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <div style={{ color: 'var(--slate)', fontSize: '13px', marginBottom: '12px' }}>Előnézet betöltése sikertelen.</div>
              <Button onClick={() => downloadFile(`/documents/${doc.id}/download`, doc.original_filename || doc.name).catch(() => toast('Letöltés sikertelen.', 'error'))}>Letöltés</Button>
            </div>
          ) : loading ? (
            <div style={{ color: 'var(--slate)', fontSize: '13px' }}>Betöltés...</div>
          ) : isPdf && blobUrl ? (
            <iframe src={blobUrl} style={{ width: '100%', height: '420px', border: 'none' }} title={doc.name} />
          ) : isImage && blobUrl ? (
            <img src={blobUrl} alt={doc.name} style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain' }} />
          ) : null}
        </div>

        {/* Metadata */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '13px' }}>
          <span style={{ color: 'var(--slate)' }}>Méret</span><span style={{ fontWeight: 600, color: 'var(--navy)', textAlign: 'right' }}>{formatSize(doc.size_bytes)}</span>
          <span style={{ color: 'var(--slate)' }}>Típus</span><span style={{ fontWeight: 600, color: 'var(--navy)', textAlign: 'right' }}>{ext.toUpperCase() || '—'}</span>
          <span style={{ color: 'var(--slate)' }}>Feltöltő</span><span style={{ fontWeight: 600, color: 'var(--navy)', textAlign: 'right' }}>{doc.uploader_name || '—'}</span>
          <span style={{ color: 'var(--slate)' }}>Dátum</span><span style={{ fontWeight: 600, color: 'var(--navy)', textAlign: 'right' }}>{formatDate(doc.created_at)}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button variant="ghost" onClick={handleClose}>Bezárás</Button>
          <Button onClick={() => downloadFile(`/documents/${doc.id}/download`, doc.original_filename || doc.name).catch(() => toast('Letöltés sikertelen.', 'error'))}>↓ Letöltés</Button>
        </div>
      </div>
    </Modal>
  );
}
